<template>
  <section class="team-section">
    <div class="section-container">
      <h2 class="section-title">核心团队</h2>
      <p class="section-desc">
        集结顶尖地质工程师与软件研发专家，用前沿技术解答大地深处的奥秘。
      </p>

      <div class="team-grid">
        <!-- 骨架屏：当没有数据时显示 -->
        <template v-if="loading">
          <div class="team-card skeleton" v-for="i in 4" :key="'skeleton-team-' + i">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-name"></div>
            <div class="skeleton-role"></div>
            <div class="skeleton-desc"></div>
            <div class="skeleton-desc short"></div>
          </div>
        </template>

        <!-- 真实数据渲染 -->
        <template v-else>
          <div class="team-card" v-for="member in teamMembers" :key="member.id">
            <div class="avatar-container">
              <div class="avatar-ring"></div>
              <img v-if="member.avatar" :src="member.avatar" :alt="member.name" class="avatar-img" />
              <div v-else class="avatar-placeholder">
                {{ getInitial(member.name) }}
              </div>
            </div>
            <h3 class="member-name">{{ member.name }}</h3>
            <div class="member-role">{{ member.role }}</div>
            <p class="member-bio">{{ member.bio }}</p>
            <div class="social-links">
              <a :href="member.link" target="_blank" class="social-icon" aria-label="LinkedIn">in</a>
              <a :href="member.github" target="_blank" class="social-icon" aria-label="Github">gh</a>
            </div>
          </div>
        </template>
      </div>

      <div class="api-note" v-if="teamMembers.length === 0 && !loading">
        <div class="note-box">
          <span class="status-dot"></span>
          等后端接口开发完毕后，将通过 Axios 自动请求团队成员数据填充于此。
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TeamSection',
  data() {
    return {
      teamMembers: [],
      loading: true
    }
  },
  mounted() {
    this.fetchTeamData();
  },
  methods: {
    async fetchTeamData() {
      try {
        const response = await axios.get('/api/team/');
        this.teamMembers = response.data;
      } catch (error) {
        console.error("Error fetching team data:", error);
      } finally {
        this.loading = false;
      }
    },
    getInitial(name) {
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    }
  }
}
</script>

<style scoped>
.team-section {
  background-color: var(--bg-color-primary);
  border-top: 1px solid rgba(15, 23, 42, 0.05);
}

.section-desc {
  text-align: center;
  color: var(--text-secondary);
  max-width: 600px;
  margin: -1.5rem auto 4rem;
  font-size: 1.1rem;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  justify-content: start;
  gap: 2.5rem;
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
}

/* Card Styles */
.team-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 23, 42, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border-radius: 20px;
  padding: 3rem 2rem 2rem;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 540px;
  max-height: 540px;
  min-height: 540px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
}

.team-card:hover {
  transform: translateY(-8px);
  background: #ffffff;
  border-color: rgba(37, 99, 235, 0.2);
  box-shadow: 0 15px 35px rgba(37, 99, 235, 0.08);
}

.avatar-container {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 1.5rem;
}

.avatar-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent, transparent, rgba(14, 165, 233, 0.3), var(--accent-color));
  animation: rotate 4s linear infinite;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.team-card:hover .avatar-ring {
  opacity: 1;
}

.avatar-img,
.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 2;
  border: 4px solid var(--bg-color-primary);
  background-color: var(--bg-color-secondary);
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--accent-color);
}

.member-name {
  font-size: 1.3rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-role {
  color: var(--accent-color);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  letter-spacing: 1px;
  width: 100%;
  text-align: center;
  flex: 0 0 auto;
  height: 2.8em;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.member-bio {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex: 0 0 auto;
  width: 100%;
  height: calc(0.9rem * 1.6 * 6);
  max-height: calc(0.9rem * 1.6 * 6);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  line-clamp: 6;
  overflow: hidden;
}

.social-links {
  display: flex;
  gap: 1rem;
  margin-top: auto;
  flex: 0 0 auto;
}

.social-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.social-icon:hover {
  background: var(--accent-color);
  color: white;
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

/* Skeleton Loading */
.skeleton {
  pointer-events: none;
}

.skeleton-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.skeleton-name {
  width: 60%;
  height: 24px;
  margin-bottom: 0.5rem;
  border-radius: 4px;
}

.skeleton-role {
  width: 40%;
  height: 16px;
  margin-bottom: 1.5rem;
  border-radius: 4px;
}

.skeleton-desc {
  width: 90%;
  height: 14px;
  margin-bottom: 0.5rem;
  border-radius: 4px;
}

.skeleton-desc.short {
  width: 70%;
}

.skeleton-avatar,
.skeleton-name,
.skeleton-role,
.skeleton-desc {
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

.skeleton-avatar {
  background: linear-gradient(90deg, #e2e8f0 0%, #cbd5e1 50%, #e2e8f0 100%);
  background-size: 200% 100%;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

/* API Note */
.api-note {
  margin-top: 3rem;
  display: flex;
  justify-content: center;
}

.note-box {
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.15);
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
  max-width: 100%;
  box-sizing: border-box;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--accent-color);
  box-shadow: 0 0 10px rgba(37, 99, 235, 0.4);
  animation: pulse 2s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4);
  }

  70% {
    box-shadow: 0 0 0 6px rgba(37, 99, 235, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
  }
}

@media (max-width: 1024px) {
  .team-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .team-grid {
    grid-template-columns: minmax(0, 1fr);
    padding: 0.5rem;
  }
}

@media (max-width: 768px) {
  .team-card {
    padding: 2rem 1.5rem;
    height: auto;
    max-height: none;
    min-height: 0;
    overflow: visible;
  }

  .member-role {
    height: auto;
  }

  .member-bio {
    height: auto;
    max-height: none;
    display: block;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    overflow: visible;
  }

  .section-desc {
    font-size: 1rem;
    margin-top: 0;
    margin-bottom: 2.5rem;
  }

  .note-box {
    padding: 0.8rem 1rem;
    font-size: 0.85rem;
    align-items: flex-start;
  }

  .status-dot {
    margin-top: 0.4rem;
  }
}
</style>
